"use client";

import {
  Box,
  BoxProps,
  Button,
  Flex,
  IconButton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";

import { useEffect, useState } from "react";
import { FaPlay } from "react-icons/fa";
import { ImTicket } from "react-icons/im";
import { BannerCarouselItemProps } from "../../../@types/Carousel";
import ShowCaseTrailerModal from "../../../modules/ShowCaseTrailerModal";
import GradeAWarn from "../../../modules/WarningModules/gradeAWarn";
import Image from "../../Image";

function CenteredBannerCarouselItem({
  bannerImg,
  showBannerAction,
  showBuyButton = false,
  bannerActionElem,
  index,
  onBuyClick,
  grade = "PG",
  gradientColor,
  imageContainerProps,
  bannerBoxProps,
  imageProps,
  trailer,
  hideTrailer = false,
  genre,
  description,
  showTitle = true,
  showGenre = true,
  showDescription = false,
  bannerTitle,
  descriptionProps,
  genreProps,
  titleProps,
  playIconProps,
  buyButtonProps,
  releaseDateProps,
  isActive,
  imageOverlayProps,
  ...props
}: BannerCarouselItemProps & {
  index: number;
  onBuyClick?: () => void;
  gradientColor?: string;
  hideTrailer?: boolean;
  imageOverlayProps?: BoxProps;
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [animKey, setAnimKey] = useState(0);
  useEffect(() => {
    if (isActive) setAnimKey((k) => k + 1);
  }, [isActive]);
  return (
    <Box
      key={`banner_carousel_item_${index}`}
      w="full"
      borderRadius="1rem"
      overflow="hidden"
      position="relative"
      width="100%"
      aspectRatio="2.7 / 1"
      {...props}
      sx={{ isolation: "isolate" }}
    >
      <Box
        position="relative"
        zIndex={1}
        height="full"
        {...imageContainerProps}
      >
        <Image
          objectFit="fill"
          w="full"
          h="full"
          objectPosition="center"
          src={bannerImg}
          fallbackSrc="/img/pv_logo_landscape.png"
          alt={`banner_slide_${index}`}
          {...imageProps}
        />
        <Box
          position="absolute"
          top="0"
          left="0"
          w="full"
          h="full"
          bg="rgba(0,0,0,0.4)"
          zIndex="1"
          {...imageOverlayProps}
        />
        {gradientColor && (
          <Box
            position="absolute"
            top="0"
            left="0"
            w="full"
            h="full"
            bg={gradientColor}
            zIndex="1"
          />
        )}
      </Box>
      {showBannerAction && (
        <Flex
          key={animKey}
          position="absolute"
          top="0"
          left="0"
          w="full"
          h="full"
          zIndex={2}
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          color="white"
          gap={4}
          p={4}
        >
          {trailer && !hideTrailer && (
            <ShowCaseTrailerModal
              url={trailer}
              actionEl={
                <IconButton
                  size="lg"
                  isRound
                  variant="solid"
                  colorScheme="whiteAlpha"
                  fontSize="2xl"
                  aria-label="Play Trailer"
                  icon={<FaPlay />}
                  {...playIconProps}
                  sx={{
                    width: "60px",
                    height: "60px",
                    bg: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(4px)",
                    _hover: {
                      bg: "rgba(255, 255, 255, 0.3)",
                      transform: "scale(1.1)",
                    },
                    transition: "all 0.3s ease",
                    ...playIconProps?.sx,
                  }}
                />
              }
            />
          )}

          <Box display={{ base: "none", md: "block" }} {...bannerBoxProps}>
            {showTitle && bannerTitle && (
              <Text
                fontSize={{ base: "xl", md: "4xl" }}
                fontWeight="extrabold"
                lineHeight="shorter"
                mb={2}
                textShadow="0 4px 20px rgba(0,0,0,0.8)"
                {...titleProps}
              >
                {bannerTitle}
              </Text>
            )}

            {((showGenre && genre) || (showDescription && description)) && (
              <Box mb={3}>
                {showGenre && genre && (
                  <Text
                    fontSize={{ base: "sm", md: "lg" }}
                    fontWeight="semibold"
                    mb={2}
                    color="gray.100"
                    letterSpacing="wide"
                    textShadow="0 4px 10px rgba(0,0,0,0.8)"
                    textTransform="uppercase"
                    {...genreProps}
                  >
                    {genre}
                  </Text>
                )}
                {showDescription && description && (
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    maxW="700px"
                    mx="auto"
                    noOfLines={2}
                    color="gray.300"
                    fontWeight="medium"
                    textShadow="0 4px 10px rgba(0,0,0,0.8)"
                    {...descriptionProps}
                  >
                    {description}
                  </Text>
                )}
                {props.release_date && (
                  <Text
                    fontSize={{ base: "xs", md: "sm" }}
                    fontWeight="bold"
                    color="primary.500"
                    mt={2}
                    textShadow="0 2px 10px rgba(0,0,0,0.8)"
                    {...releaseDateProps}
                  >
                    {new Date(props.release_date).toDateString()}
                  </Text>
                )}
              </Box>
            )}
          </Box>

          {showBuyButton && (
            <Button
              onClick={() => {
                if (grade === "A") {
                  onOpen();
                } else {
                  onBuyClick?.();
                }
              }}
              leftIcon={<ImTicket />}
              size="lg"
              px={8}
              variant="outline"
              color="white"
              borderColor="primary.200"
              background="primary.500"
              borderRadius="full"
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "lg",
                bg: "primary.500",
                borderColor: "primary.500",
              }}
              transition="all 0.2s"
              {...buyButtonProps}
            >
              Book Now
            </Button>
          )}

          <> {bannerActionElem}</>
          <GradeAWarn
            isOpen={isOpen}
            onClose={onClose}
            onProceed={onBuyClick as () => void}
            modalKey={"warning_module_banner"}
          />
        </Flex>
      )}
    </Box>
  );
}

export default CenteredBannerCarouselItem;
